import React, { useState } from 'react'
import { Card, Typography, Button, Space, Spin, Empty, Modal, DatePicker, Select, message } from 'antd'
import { CalendarOutlined, VideoCameraOutlined, HomeOutlined, MedicineBoxOutlined } from '@ant-design/icons'
import { useSelector, useDispatch } from 'react-redux'
import dayjs from 'dayjs'
import useNextAppointment from '../hooks/useNextAppointment'
import { handleClinicBooking } from '../../../../../Bookings/utils/lib'
import { CARD_STYLES, CARD_GRADIENTS } from '../utils/constants'
import BookingSummaryModal from '../../../../../Bookings/components/BookingSummary'
import DiagnosisOverview from '../../../../../Medical History/components/DiagnosisOverview'
import coreReducer from '../../../../../../core/utils/reducer'
import CoreEnums from '../../../../../../core/utils/enums'
import { updateBooking, generateAgoraRtcToken } from '../../../../../Bookings/utils/utils'
import { generateUniqueUIDFromString, handleError } from '../../../../../lib/utils'
import { generateTimeSlots } from '../../../../../Appointments-Calendar/full-calendar-components/Viewing Record/utils'

const { Title, Text, Paragraph } = Typography

const NextAppointmentCard = () => {
  const { nextAppointment, loading, error, refetch } = useNextAppointment()
  const state = useSelector((state) => state)
  const dispatch = useDispatch()

  // Modal states
  const [bookingOpen, setBookingOpen] = useState(false)
  const [rescheduling, setRescheduling] = useState(false)
  const [rescheduleDate, setRescheduleDate] = useState(null)
  const [rescheduleTime, setRescheduleTime] = useState(null)
  const [rescheduleLoading, setRescheduleLoading] = useState(false)

  // Agora states for virtual consultations
  const [joined, setJoined] = useState(false)
  const [agoraToken, setAgoraToken] = useState(null)
  const [agoraChannel, setAgoraChannel] = useState(null)
  const [agoraUid, setAgoraUid] = useState(null)

  const getConsultationType = (appointment) => {
    if (!appointment) return { text: 'No Appointment', icon: CalendarOutlined }

    if (appointment.isVirtualVisit) {
      const serviceData =
        state.services && state.services.data ? state.services.data.find((s) => s._id === appointment.service) : null

      const isFlexiOrPriority = !appointment.medicalProf && !appointment.startTime
      let serviceName = (appointment.serviceName || '').toLowerCase()

      if (!serviceName && appointment.service && state.services && state.services.data) {
        const serviceData = state.services.data.find((s) => s._id === appointment.service)
        if (serviceData) {
          serviceName = (serviceData.name || serviceData.label || '').toLowerCase()
        }
      }

      if (
        appointment.isPriorityConsult ||
        appointment.serviceType === 'priority' ||
        appointment.consultType === 'priority' ||
        serviceName.includes('priority') ||
        serviceName.includes('urgent') ||
        serviceName.includes('immediate')
      ) {
        return { text: 'Priority Virtual', icon: VideoCameraOutlined, color: '#ff4d4f' }
      } else if (
        appointment.serviceType === 'flexi' ||
        appointment.consultType === 'flexi' ||
        serviceName.includes('flexi') ||
        (isFlexiOrPriority && !serviceName.includes('priority'))
      ) {
        return { text: 'Flexi Virtual', icon: VideoCameraOutlined, color: '#52c41a' }
      } else {
        return { text: 'Scheduled Virtual', icon: VideoCameraOutlined, color: '#1890ff' }
      }
    } else if (appointment.clinicRef === 'Home Visit') {
      return { text: 'Home Visit', icon: HomeOutlined, color: '#fa8c16' }
    } else {
      return { text: 'In-Person', icon: MedicineBoxOutlined, color: '#722ed1' }
    }
  }

  const formatAppointmentDateTime = (appointment) => {
    if (!appointment) return ''

    const displayTime = appointment.startTime || dayjs(appointment.dateCreated).format('HH:mm')

    let displayDate
    if (appointment.bookingDate && dayjs(appointment.bookingDate).isValid()) {
      displayDate = dayjs(appointment.bookingDate).format('DD MMM YYYY')
    } else if (appointment.dateCreated && dayjs(appointment.dateCreated).isValid()) {
      displayDate = dayjs(appointment.dateCreated).format('DD MMM YYYY')
    } else {
      displayDate = 'Date TBD'
    }

    return `${displayDate}, ${displayTime}`
  }

  const getPractitionerName = (appointment) => {
    return appointment?.medicalProfName || 'Meta-Health Verified Doctor'
  }

  const getClinicName = (appointment) => {
    if (!appointment) return 'Meta-Health Clinic'

    if (appointment.isVirtualVisit) {
      // For virtual visits, show clinic only if scheduled with specific practitioner
      if (appointment.medicalProf && appointment.startTime) {
        return appointment.clinicName || 'Meta-Health Virtual Clinic'
      } else {
        return 'Meta-Health Virtual Clinic'
      }
    } else if (appointment.clinicRef === 'Home Visit') {
      return 'Home Visit Service'
    } else {
      return appointment.clinicName || 'Meta-Health Clinic'
    }
  }

  const handleViewDetails = (appointment) => {
    if (appointment.status !== CoreEnums.bookingStatuses.completed) {
      setBookingOpen(true)
    } else {
      dispatch(
        coreReducer.actions.addTab({
          key: `${CoreEnums.tabKeys.MY_BOOKINGS}_${appointment._id}`,
          closable: true,
          label: 'Appointment Details',
          children: <DiagnosisOverview data={appointment} />
        })
      )
    }
  }

  const handleReschedule = () => {
    setRescheduling(true)
  }

  const joinVirtualConsult = async (record) => {
    try {
      const agoraCreds = await generateAgoraRtcToken(
        record._id,
        generateUniqueUIDFromString(state.auth.agiliteUser._id)
      )
      setAgoraToken(agoraCreds.token)
      setAgoraChannel(agoraCreds.channelName)
      setAgoraUid(agoraCreds.uid)
      setJoined(true)
    } catch (e) {
      message.error(handleError(e, true))
    }
  }

  const confirmReschedule = async () => {
    if (!rescheduleDate || !rescheduleTime) {
      message.error('Please select both date and time')
      return
    }

    // Validation for patient rules
    if (state.auth.agiliteUser?.extraData?.role?.type === 'patient') {
      const appointmentDate = dayjs(nextAppointment.bookingDate)
      const [hours, minutes] = nextAppointment.startTime.split(':')
      const appointmentDateTime = appointmentDate.hour(parseInt(hours)).minute(parseInt(minutes)).second(0)

      const currentTime = dayjs()
      const hoursUntilAppointment = appointmentDateTime.diff(currentTime, 'hours', true)
      const appointmentInPast = appointmentDateTime.isBefore(currentTime)

      if (appointmentInPast) {
        message.error('Cannot reschedule - appointment has already passed.')
        return
      }

      if (hoursUntilAppointment < 1) {
        message.error(
          `Cannot reschedule - appointment is in ${hoursUntilAppointment.toFixed(
            1
          )} hours. Must be 1+ hours in advance.`
        )
        return
      }
    }

    setRescheduleLoading(true)
    try {
      // Find service info for calculating end time
      const serviceInfo = state.services.data.find((i) => i._id === nextAppointment.service)
      if (!serviceInfo) {
        message.error('Service information not found')
        return
      }

      await updateBooking(
        {
          bookingDate: dayjs(rescheduleDate).format('YYYY-MM-DD'),
          startTime: rescheduleTime,
          endTime: dayjs(rescheduleTime, 'HH:mm').add(serviceInfo.timeslotInterval, 'minutes').format('HH:mm')
        },
        { _id: nextAppointment._id }
      )

      message.success('Appointment rescheduled successfully')
      resetRescheduleData()
      refetch() // Refresh the next appointment data
    } catch (e) {
      message.error(handleError(e, true))
    }
    setRescheduleLoading(false)
  }

  const resetRescheduleData = () => {
    setRescheduleDate(null)
    setRescheduleTime(null)
    setRescheduling(false)
  }

  const consultationType = getConsultationType(nextAppointment)

  if (loading) {
    return (
      <Card
        title='Next Appointment'
        bordered={false}
        style={{
          boxShadow: CARD_STYLES.shadow,
          borderRadius: CARD_STYLES.borderRadius,
          height: '100%'
        }}
        headStyle={{
          background: CARD_GRADIENTS.green,
          color: 'white',
          borderRadius: '12px 12px 0 0',
          fontWeight: '600'
        }}
      >
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size='large' />
          <Paragraph style={{ marginTop: '16px', color: '#666' }}>Loading your next appointment...</Paragraph>
        </div>
      </Card>
    )
  }

  if (error) {
    return (
      <Card
        title='Next Appointment'
        bordered={false}
        style={{
          boxShadow: CARD_STYLES.shadow,
          borderRadius: CARD_STYLES.borderRadius,
          height: '100%'
        }}
        headStyle={{
          background: '#ff4d4f',
          color: 'white',
          borderRadius: '12px 12px 0 0',
          fontWeight: '600'
        }}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Text type='danger'>Unable to load appointment</Text>
        </div>
      </Card>
    )
  }

  if (!nextAppointment) {
    return (
      <Card
        title='Next Appointment'
        bordered={false}
        style={{
          boxShadow: CARD_STYLES.shadow,
          borderRadius: CARD_STYLES.borderRadius,
          height: '100%'
        }}
        headStyle={{
          background: '#d9d9d9',
          color: '#666',
          borderRadius: '12px 12px 0 0',
          fontWeight: '600'
        }}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<span style={{ color: '#666' }}>No upcoming appointments</span>}
          />
          <Button type='primary' style={{ marginTop: '16px' }} onClick={() => handleClinicBooking(false, dispatch)}>
            Schedule Appointment
          </Button>
        </div>
      </Card>
    )
  }

  return (
    <Card
      title='Next Appointment'
      bordered={false}
      style={{
        boxShadow: CARD_STYLES.shadow,
        borderRadius: CARD_STYLES.borderRadius,
        height: '100%'
      }}
      headStyle={{
        background: CARD_GRADIENTS.green,
        color: 'white',
        borderRadius: '12px 12px 0 0',
        fontWeight: '600'
      }}
    >
      <div style={{ marginBottom: '16px' }}>
        <Text strong style={{ fontSize: '12px', color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Doctor
        </Text>
        <Title level={5} style={{ margin: '4px 0 0 0', lineHeight: '1.2' }}>
          {getPractitionerName(nextAppointment)}
        </Title>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <Text strong style={{ fontSize: '12px', color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Consultation Type
        </Text>
        <div style={{ marginTop: '4px' }}>
          <Text style={{ fontSize: '14px', fontWeight: '500' }}>
            {React.createElement(consultationType.icon, {
              style: { marginRight: '8px', color: consultationType.color || '#1890ff' }
            })}
            {consultationType.text}
          </Text>
        </div>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <Text strong style={{ fontSize: '12px', color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Facility
        </Text>
        <div style={{ marginTop: '4px' }}>
          <Text style={{ fontSize: '14px', fontWeight: '500' }}>{getClinicName(nextAppointment)}</Text>
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <Text strong style={{ fontSize: '12px', color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Date & Time
        </Text>
        <div style={{ marginTop: '4px' }}>
          <Text style={{ fontSize: '14px', fontWeight: '500' }}>
            <CalendarOutlined style={{ marginRight: '8px', color: '#52c41a' }} />
            {nextAppointment.bookingDate && dayjs(nextAppointment.bookingDate).isValid()
              ? dayjs(nextAppointment.bookingDate).format('dddd, DD MMM YYYY')
              : nextAppointment.dateCreated && dayjs(nextAppointment.dateCreated).isValid()
              ? dayjs(nextAppointment.dateCreated).format('dddd, DD MMM YYYY')
              : 'Date TBD'}{' '}
            at{' '}
            <Text style={{ fontSize: '16px', fontWeight: '600', color: '#1890ff' }}>
              {nextAppointment.startTime || dayjs(nextAppointment.dateCreated).format('HH:mm')}
            </Text>
          </Text>
        </div>
      </div>

      <Space>
        <Button type='primary' onClick={() => handleViewDetails(nextAppointment)}>
          View Details
        </Button>
        <Button onClick={handleReschedule}>Reschedule</Button>
      </Space>

      {/* Booking Summary Modal */}
      <BookingSummaryModal
        joinVirtualConsult={joinVirtualConsult}
        data={nextAppointment}
        setData={() => {}} // Not used in this context
        modalOpen={bookingOpen}
        setModalOpen={setBookingOpen}
        joined={joined}
        setJoined={setJoined}
        agoraToken={agoraToken}
        agoraChannel={agoraChannel}
        agoraUid={agoraUid}
      />

      {/* Reschedule Modal */}
      <Modal
        title='Reschedule Appointment'
        open={rescheduling}
        onOk={confirmReschedule}
        onCancel={resetRescheduleData}
        okText='Confirm Reschedule'
        cancelText='Cancel'
        confirmLoading={rescheduleLoading}
        width={600}
      >
        {nextAppointment && (
          <>
            <div style={{ marginBottom: 16 }}>
              <strong>Current Appointment:</strong>
              <br />
              {dayjs(nextAppointment.bookingDate).format('MMMM DD, YYYY')} at {nextAppointment.startTime}
            </div>

            <Space direction='vertical' style={{ width: '100%' }} size='large'>
              <div>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>New Date:</label>
                <DatePicker
                  disabledDate={(current) => current && current < dayjs().startOf('day')}
                  style={{ width: '100%' }}
                  onChange={setRescheduleDate}
                  value={rescheduleDate}
                  size='large'
                  placeholder='Choose new date'
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>New Time:</label>
                <Select
                  disabled={!rescheduleDate}
                  placeholder='Choose new time'
                  style={{ width: '100%' }}
                  onChange={setRescheduleTime}
                  value={rescheduleTime}
                  size='large'
                  options={generateTimeSlots().map((timeslot) => ({
                    value: timeslot,
                    label: timeslot
                  }))}
                />
              </div>
            </Space>
          </>
        )}
      </Modal>
    </Card>
  )
}

export default NextAppointmentCard
